'use client';

import {
  Button,
  type ButtonProps,
  EmptyState,
  MetaRow,
  Tag,
  type TagProps,
} from '@lulalife/design-system';
import { Inbox01 } from '@lulalife/design-system/icons/line';
import type { HTMLAttributes, ReactNode } from 'react';
import { DSSurface } from './DSSurface';
import { SlideoutContentHeader } from './SlideoutContentHeader';

/**
 * The record tabs of the work order slideout (Trips, Quotes, Invoices) on DS parts: Figma section
 * 7460:2364, frames 7481:11115, 7481:12278, 7481:13448 and their empties 7501:6915, 7501:7427,
 * 7501:7906. `Slideout / Panel` content (16 padding, blocks 16 apart), a Section Header with its
 * Top Right buttons, then the cards or the centred Empty State.
 */

/**
 * The tab's panel. It grows to fill the tab column so an Empty State can centre in what is left.
 * Holds a `SlideoutTabHeader`, then a `SlideoutTabBody`.
 */
export const SlideoutTabPanel = ({
  children,
  'data-testid': testId,
}: {
  children: ReactNode;
  'data-testid'?: string;
}) => (
  <DSSurface
    className="flex flex-1 flex-col gap-space-16 bg-bg-surface p-space-16 font-sans"
    data-testid={testId}
  >
    {children}
  </DSSurface>
);

/**
 * The Section Header: title, Top Right buttons, and Back on phones (`close-panel-btn` hides it on
 * desktop, as the old heads did). Built on the local `SlideoutContentHeader`: the DS `Section`
 * header aligns its title to the top of a 36 px button row, where the frames centre it.
 */
export const SlideoutTabHeader = ({
  title,
  titleTestId,
  onBack,
  action,
  buttonRow = true,
}: {
  title: string;
  titleTestId?: string;
  onBack?: () => void;
  /** The Top Right slot. */
  action?: ReactNode;
  /** Off for a tab that never has header buttons: the row is the title's 24, not 36. */
  buttonRow?: boolean;
}) => (
  <SlideoutContentHeader
    title={<span data-testid={titleTestId}>{title}</span>}
    onBack={onBack}
    backClassName="close-panel-btn"
    right={action}
    buttonRow={buttonRow}
  />
);

/** The cards, 16 apart, or the Empty State filling the rest of the panel. */
export const SlideoutTabBody = ({
  children,
  'data-testid': testId,
}: {
  children: ReactNode;
  'data-testid'?: string;
}) => (
  <div className="flex flex-1 flex-col gap-space-16" data-testid={testId}>
    {children}
  </div>
);

/** A tab with nothing in it: the DS Empty State (inbox icon, title, no description), centred both ways. */
export const SlideoutTabEmpty = ({
  title,
  action,
  'data-testid': testId,
}: {
  title: string;
  action?: ReactNode;
  'data-testid'?: string;
}) => (
  <div className="flex flex-1 items-center justify-center p-space-24" data-testid={testId}>
    <EmptyState icon={<Inbox01 />} title={title} description={null} action={action} />
  </div>
);

type SlideoutRecordCardProps = Omit<HTMLAttributes<HTMLDivElement>, 'title'> & {
  title: ReactNode;
  titleTestId?: string;
  /** Top Right, left to right: the status Tag, then the More button. */
  status?: ReactNode;
  more?: ReactNode;
  /** The card's body: meta rows, notes, buttons, the total row. */
  children?: ReactNode;
  /** The 12/16 tertiary line at the foot (Trip Card's "Last updated"). */
  footer?: ReactNode;
  /**
   * The header holds only text and a Tag, so it is 24 high (Invoice Card 8245:3372). Off, it keeps
   * the 36 row the Trip and Quote frames draw for their buttons.
   */
  compactHeader?: boolean;
};

/**
 * The frames' Trip, Quote and Invoice Card: white, 1 px `card/border`, 8 radius, 12 above and below,
 * the header (16/24 title, Top Right) then the body 12 apart, 12 in from the sides.
 *
 * DS-GAP(card-bootstrap-collision): DS `Card` roots on the `card` class, which Bootstrap's own
 * unlayered `.card` (imported for legacy markup) restyles inside DS regions too (border, radius,
 * background, height). So the card is drawn from the DS card tokens here, as `ContactCard` is.
 */
export const SlideoutRecordCard = ({
  title,
  titleTestId,
  status,
  more,
  children,
  footer,
  compactHeader = false,
  className,
  ...props
}: SlideoutRecordCardProps) => (
  <DSSurface
    {...props}
    className={[
      'flex w-full flex-col gap-space-12 overflow-hidden rounded-card-radius border border-card-border bg-card-bg py-space-12 font-sans',
      className,
    ]
      .filter(Boolean)
      .join(' ')}
  >
    <div
      className={[
        'flex w-full items-center justify-between px-space-12',
        compactHeader ? null : 'min-h-[36px]',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* biome-ignore lint/a11y/useSemanticElements: Bootstrap's unlayered h1-h6 rules would beat the DS type utilities. */}
      <div
        role="heading"
        aria-level={4}
        className="min-w-0 truncate text-card-title mobile-heading-sm-base md:desktop-heading-sm-base"
        data-testid={titleTestId}
      >
        {title}
      </div>
      {status || more ? (
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-space-2 pl-space-12">
          {status}
          {more}
        </div>
      ) : null}
    </div>
    {children ? (
      <div className="flex w-full flex-col gap-space-12 px-space-12">{children}</div>
    ) : null}
    {footer ? (
      <div className="flex w-full items-center gap-space-8 px-space-12 text-text-tertiary mobile-body-sm md:desktop-body-sm">
        {footer}
      </div>
    ) : null}
  </DSSurface>
);

/** The card's status: a compact DS Tag in the status's tone, carrying the caller's test id. */
export const SlideoutRecordStatus = ({
  label,
  color,
  icon,
  'data-testid': testId,
}: {
  label: string | null | undefined;
  color: TagProps['color'];
  icon?: TagProps['icon'];
  'data-testid'?: string;
}) =>
  label ? (
    <span className="inline-flex" data-testid={testId}>
      <Tag size="compact" color={color} label={label} icon={icon} />
    </span>
  ) : null;

/** The card's meta rows, 8 apart. */
export const SlideoutRecordRows = ({ children }: { children: ReactNode }) => (
  <div className="flex w-full flex-col gap-space-8">{children}</div>
);

/**
 * One meta row: DS `MetaRow` (Split), a 20 px icon and the key in the 140 px left column, the value
 * on the right.
 * DS-GAP (local override, as `OverviewMetaRow`): the package draws the icon 16 px on desktop where the
 * frames draw 20, so the SVG is held at 20 here.
 */
export const SlideoutRecordRow = ({
  icon,
  label,
  children,
  'data-testid': testId,
  valueTestId,
}: {
  icon: ReactNode;
  label: ReactNode;
  children: ReactNode;
  'data-testid'?: string;
  valueTestId?: string;
}) => (
  <MetaRow
    layout="split"
    icon={icon}
    label={label}
    className="[&>div:first-child_svg]:size-[20px]"
    data-testid={testId}
    value={<div data-testid={valueTestId}>{children}</div>}
  />
);

/** The "Quote total" / "Total Amount" row: a top border, 8 above and below, label left, amount right. */
export const SlideoutRecordTotal = ({
  label,
  amount,
  amountTestId,
}: {
  label: ReactNode;
  amount: ReactNode;
  amountTestId?: string;
}) => (
  <div className="flex w-full items-center justify-between gap-space-12 border-t border-border-subtle py-space-8 text-text-primary mobile-body-md-strong md:desktop-body-md-strong">
    <span className="min-w-0">{label}</span>
    <span className="shrink-0" data-testid={amountTestId}>
      {amount}
    </span>
  </div>
);

/** A line of 14/20 secondary text inside a card (who a vendor offer went to, a QA note). */
export const SlideoutRecordNote = ({
  children,
  'data-testid': testId,
}: {
  children: ReactNode;
  'data-testid'?: string;
}) => (
  <div
    className="text-text-secondary mobile-body-md-base md:desktop-body-md-base"
    data-testid={testId}
  >
    {children}
  </div>
);

/** A row of buttons inside a card or an Empty State, 8 apart. */
export const SlideoutRecordActions = ({ children }: { children: ReactNode }) => (
  <div className="flex flex-wrap items-center gap-space-8">{children}</div>
);

/** The disabled look for `aria-disabled`, per variant: the package styles only the native attribute. */
const ARIA_DISABLED_CLASSES = {
  primary:
    'aria-disabled:cursor-not-allowed aria-disabled:border-button-primary-bg-disabled aria-disabled:bg-button-primary-bg-disabled aria-disabled:text-button-primary-text-disabled aria-disabled:hover:bg-button-primary-bg-disabled',
  secondary:
    'aria-disabled:cursor-not-allowed aria-disabled:border-button-secondary-bg-disabled aria-disabled:bg-button-secondary-bg-disabled aria-disabled:text-button-secondary-text-disabled aria-disabled:hover:bg-button-secondary-bg-disabled',
} as const;

/**
 * A DS Button for `ButtonWithDisabledReason` (ORI-674): blocked with a reason, it stays focusable
 * with `aria-disabled` and a tooltip, so it needs the disabled look on that attribute.
 * DS-GAP(button-aria-disabled): DS Button draws its disabled state from `:disabled` only.
 */
export const DSReasonButton = ({
  variant = 'secondary',
  className,
  ...props
}: ButtonProps & { variant?: keyof typeof ARIA_DISABLED_CLASSES }) => (
  <Button
    variant={variant}
    className={[ARIA_DISABLED_CLASSES[variant], className].filter(Boolean).join(' ')}
    {...props}
  />
);
